/**
 * OLLO corner stamp for saved captures — pure 2D-canvas compositing.
 *
 * No PlayCanvas, no DOM lookups, no i18n: the mark is a brand asset, not copy,
 * and keeping this module dependency-free means it can run inside the same
 * synchronous task as the framebuffer readback (see capture-frame.ts — the
 * whole design hinges on tap → render → readback → watermark → encode → share
 * never yielding to the event loop).
 *
 * WHY Path2D and not an <img src="logo.svg">: an Image needs a decode (async,
 * which breaks the single-task rule), it adds a request that can 404 under the
 * real `--base=/viewer/` subpath deploy, and logo.svg's paths carry no `fill`
 * so it would need the same `filter: invert(1)` hack .ollo-branding-logo does
 * (viewer.css:384). With Path2D the fill is just set.
 *
 * The four `d` strings are the Olloport mark and are duplicated in three other
 * places — KEEP IN SYNC with:
 *   webpages/viewer2/src/ui/loading-screen.ts (OLLO_LOGO_SVG)
 *   webpages/viewer2/public/logo.svg
 *   webpages/website/src/components/HomePage.astro
 *
 * WHY THERE IS A DARK PLATE BEHIND THE GLYPHS, measured rather than assumed.
 * The first cut of this module used the on-screen recipe scaled up — white
 * glyphs at 0.92 alpha over a proportional dark drop shadow, no plate. Rendered
 * over a Comedor-like near-white field (walls ~235 luminance) and measured, a
 * 0.92-white glyph composites to ~254: WCAG contrast 1.14:1. The mark was
 * legible ONLY because of its blurred halo, i.e. the ink carried no contrast of
 * its own. Downscale that frame to the 200px-wide thumbnail a forwarded image
 * actually becomes and the halo averages back into the wall: measured Michelson
 * contrast over the stamp box fell to 0.148 with the visible result being a
 * grey smudge — no readable OLLO, no readable wordmark. Since surviving
 * *scaling* is the stated job of this stamp (the filename carries the
 * crop-proof copy of the attribution), the ink now sits on its own ground: a
 * rounded, semi-dark plate. On a lit wall the plate supplies the contrast; on a
 * near-black scene the plate is imperceptible and the white glyphs carry
 * themselves. One fixed recipe, both extremes, and nothing here samples the
 * image — so this module still needs no pixel readback of its own.
 */

const LOGO_VIEWBOX_W = 751.48;
const LOGO_VIEWBOX_H = 329.5;
const LOGO_ASPECT = LOGO_VIEWBOX_W / LOGO_VIEWBOX_H; // 2.2807

const LOGO_PATHS = [
    'M360.29,45.62v244.69c0,1.38-.55,2.7-1.52,3.68l-39.2,33.1c-1.83,1.54-4.14,2.39-6.53,2.39l-50.44.03c-2.87,0-5.2-2.33-5.2-5.2V5.2C257.4,2.33,259.72,0,262.6,0h52.3C316.27,0,317.59.55,318.56,1.52l40.2,40.42c.98.98,1.53,2.3,1.53,3.68Z',
    'M391.18,45.62v244.69c0,1.38.55,2.7,1.52,3.68l39.2,33.1c1.83,1.54,4.14,2.39,6.53,2.39h50.41c2.87,0,5.2-2.33,5.2-5.2l.03-319.07C494.08,2.33,491.75,0,488.88,0h-52.3c-1.38,0-2.7.55-3.67,1.52l-40.2,40.42c-.98.98-1.53,2.3-1.53,3.68Z',
    // The two O's carry their counters as subpaths of the SAME d string — one
    // Path2D each, so default nonzero winding punches the holes. Never split.
    'M0,5.2v319.07c0,2.87,2.33,5.2,5.2,5.2h216.11c2.87,0,5.2-2.33,5.2-5.2V5.21c0-2.87-2.33-5.2-5.2-5.2L5.2,0C2.33,0,0,2.33,0,5.2ZM72.07,252.19V77.27c0-2.87,2.33-5.2,5.2-5.2h61.67c2.87,0,5.2,2.33,5.2,5.2v174.93c0,2.87-2.33,5.2-5.2,5.2h-61.67c-2.87,0-5.2-2.33-5.2-5.2Z',
    'M746.28,0l-216.11.02c-2.87,0-5.2,2.33-5.2,5.2v319.05c0,2.87,2.33,5.2,5.2,5.2h216.11c2.87,0,5.2-2.33,5.2-5.2V5.2c0-2.87-2.33-5.2-5.2-5.2ZM674.21,257.39h-61.67c-2.87,0-5.2-2.33-5.2-5.2V77.27c0-2.87,2.33-5.2,5.2-5.2h61.67c2.87,0,5.2,2.33,5.2,5.2v174.93c0,2.87-2.33,5.2-5.2,5.2Z'
];

/** The captured image leaves the site, so the stamp carries a destination a
 *  stranger can act on rather than the "powered by" relationship label the
 *  on-screen branding uses. */
const WORDMARK = 'OLLOPORT.COM';

/** viewer2 ships no @font-face and index.html loads no webfont, so
 *  'Instrument Sans' does not exist in this document — asking for it would
 *  silently fall back anyway. System stack, and no document.fonts await. */
const FONT_STACK = 'system-ui, -apple-system, sans-serif';

/** Tracking to match the on-screen wordmark. Firefox has no ctx.letterSpacing;
 *  a tracking difference across 12 glyphs is cosmetically invisible, and the
 *  fit below measures whatever the engine actually does, so both engines end up
 *  with a wordmark exactly as wide as the logo above it. */
const TRACKING_EM = 0.14;

type LetterSpacingCtx = CanvasRenderingContext2D & { letterSpacing?: string };

/** Rounded rect via arcTo — Path2D.roundRect is Safari 16.4+, and this stamp
 *  has to composite identically on every engine that can run the viewer. */
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

/**
 * Stamp the mark bottom-right on an already-populated 2D context.
 *
 * Bottom-RIGHT on purpose: .ollo-branding lives bottom-LEFT as a faint link
 * affordance, so a near-opaque stamp in that corner reads as "the UI got baked
 * into my photo" — the exact confusion a chrome-free capture exists to avoid.
 * Bottom-right is the phone-camera watermark convention, and in an
 * orbit-framed interior the bottom corners are the emptiest region.
 *
 * Everything is a FRACTION of the captured size, never a fixed px count: the
 * on-screen mark is 24px tall whatever the canvas is, but a capture can be a
 * 320px client embed or a 3440px ultrawide desktop.
 */
export function drawWatermark(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const short = Math.min(w, h);

    // Clamps are legibility limits, not taste: each O's counter is a 72-unit
    // square in the 751-unit box (~9.6% of markW), so at markW 72 the holes are
    // still ~7px and the O's read as letters; much below that the mark
    // degenerates into four blobs. The 200 ceiling stops a big capture getting a
    // billboard. NOTE the order: the 0.28*w frame guard is applied LAST and so
    // WINS over the 72 floor below a ~257px-wide capture — deliberate, because a
    // mark covering a third of a tiny embed is a worse outcome than a small one.
    const markW = Math.min(Math.max(0.18 * short, 72), 0.28 * w, 200);
    const markH = markW / LOGO_ASPECT;
    const scale = markW / LOGO_VIEWBOX_W;
    const inset = Math.max(12, Math.round(0.028 * short));

    ctx.save();
    ctx.textAlign = 'right';
    ctx.textBaseline = 'alphabetic';

    const lsCtx = ctx as LetterSpacingCtx;
    const tracked = 'letterSpacing' in lsCtx;
    if (tracked) lsCtx.letterSpacing = `${TRACKING_EM}em`;

    // Size the wordmark to the logo instead of guessing a ratio: at a fixed
    // 0.42*markH it measured ~1.5x wider than the mark it sits under, so the
    // two-line block read as ragged rather than as one stamp. Measuring also
    // absorbs whatever the system font stack resolves to per platform.
    ctx.font = `300 100px ${FONT_STACK}`;
    const refW = ctx.measureText(WORDMARK).width;
    const textPx = Math.max(8, Math.round(refW > 0 ? (markW / refW) * 100 : markH * 0.3));
    ctx.font = `300 ${textPx}px ${FONT_STACK}`;
    // Chrome counts the tracking after the LAST glyph in the advance width, so
    // right-aligned ink stops short of the anchor by exactly that much. Nudging
    // the anchor keeps the ink flush with the plate's padding instead of
    // leaving a third of the padding as dead space on one side only.
    const trail = tracked ? TRACKING_EM * textPx : 0;
    // The plate is sized from what the engine ACTUALLY measured, not from
    // markW: rounding textPx to whole pixels (and the 8px floor biting on a
    // small embed) can leave the wordmark a few px wider than the logo, and a
    // plate sized off markW would then be overflowed by its own contents.
    const inkW = Math.max(markW, ctx.measureText(WORDMARK).width - trail);

    // Cap height, not em size: the plate is packed around the ink, and caps
    // (there are no descenders in OLLOPORT.COM) sit ~0.72 em above baseline.
    const capH = textPx * 0.72;
    const gap = markH * 0.26;
    const padX = Math.max(4, Math.round(markH * 0.17));
    const padY = Math.max(3, Math.round(markH * 0.15));

    const plateW = inkW + 2 * padX;
    const plateH = markH + gap + capH + 2 * padY;
    const plateX = w - inset - plateW;
    const plateY = h - inset - plateH;
    // Both lines are right-aligned to the same edge, so the block reads as one
    // stamp rather than two stacked things.
    const contentRight = plateX + padX + inkW;
    const markX = contentRight - markW;
    const markY = plateY + padY;
    const baseline = markY + markH + gap + capH;

    // The plate is what makes the mark survive being shrunk (see the module
    // header). 0.34 black is enough to hold ~0.2 Michelson against a lit wall at
    // any scale, and is imperceptible over the near-black scenes where the old
    // white-on-shadow recipe already worked. Its own soft shadow keeps the edge
    // from reading as a pasted-on sticker.
    ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
    ctx.shadowBlur = Math.max(2, 0.14 * markH);
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = Math.max(1, 0.04 * markH);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.34)';
    roundRect(ctx, plateX, plateY, plateW, plateH, Math.min(plateH * 0.28, markH * 0.34));
    ctx.fill();

    // Ink is drawn with the shadow OFF: on the plate it needs no separation, and
    // a halo under 0.96-white glyphs only softens their edges — which is what
    // the counters of the two O's are made of.
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    ctx.save();
    ctx.translate(markX, markY);
    ctx.scale(scale, scale);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
    for (const d of LOGO_PATHS) ctx.fill(new Path2D(d));
    ctx.restore();

    ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
    ctx.fillText(WORDMARK, contentRight + trail, baseline);

    ctx.restore();
}
